import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { ThemeToggle } from '../components/layout/ThemeToggle';
import { fetchNotificationPreferences, updateNotificationPreferences } from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import type { NotificationPreferences } from '../types';

export function SettingsPage(): JSX.Element {
  const [density, setDensity] = useState<'comfortable' | 'compact'>('comfortable');
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: preferences, isLoading } = useQuery<NotificationPreferences | undefined>({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      if (!user) return undefined;
      return fetchNotificationPreferences();
    },
    enabled: Boolean(user),
    staleTime: 1000 * 60 * 10,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Partial<NotificationPreferences>) => updateNotificationPreferences(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });

  const togglePreference = (key: keyof NotificationPreferences) => {
    if (!preferences) return;
    updateMutation.mutate({ [key]: !preferences[key] } as Partial<NotificationPreferences>);
  };

  return (
    <div className="space-y-6">
      <header className="glass-card p-6">
        <h2 className="text-xl font-semibold text-foreground">Workspace settings</h2>
        <p className="text-sm text-muted-foreground">Tune the InspectAI experience for your team.</p>
      </header>

      <section className="glass-card space-y-6 p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Appearance</h3>
            <p className="text-sm text-muted-foreground">Switch theme and adjust density for your workflow.</p>
          </div>
          <ThemeToggle />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <input
              type="radio"
              name="density"
              value="comfortable"
              checked={density === 'comfortable'}
              onChange={() => setDensity('comfortable')}
            />
            Comfortable
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <input type="radio" name="density" value="compact" checked={density === 'compact'} onChange={() => setDensity('compact')} />
            Compact
          </label>
        </div>
      </section>

      <section className="glass-card space-y-6 p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Report templates</h3>
            <p className="text-sm text-muted-foreground">
              Configure inspection report layouts by uploading samples or defining JSON schemas.
            </p>
          </div>
          <Link
            to="/app/settings/presets"
            className="btn-glass btn-secondary focus-ring text-sm font-semibold"
          >
            Manage report presets
          </Link>
        </div>
      </section>

      <section className="glass-card space-y-6 p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Notifications & communication</h3>
            <p className="text-sm text-muted-foreground">Control how InspectAI keeps you informed.</p>
          </div>
        </div>
        {isLoading || !preferences ? (
          <div className="space-y-3">
            {[...Array(4).keys()].map((key) => (
              <div key={key} className="h-12 animate-pulse rounded-xl bg-white/40 dark:bg-white/10" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {(
              [
                {
                  key: 'productUpdates' as const,
                  title: 'Product updates',
                  description: 'Release notes, new features, and changes to the InspectAI platform.',
                },
                {
                  key: 'marketingEmails' as const,
                  title: 'Promotions & partner offers',
                  description: 'Occasional promotions and partner content tailored for property professionals.',
                },
                {
                  key: 'newsletter' as const,
                  title: 'Latest newsletters',
                  description: 'Monthly round-up of inspection best practices and AI tips.',
                },
                {
                  key: 'reminders' as const,
                  title: 'Task reminders',
                  description: 'Receive reminders about upcoming inspections or pending reports.',
                },
              ]
            ).map((item) => (
              <label
                key={item.key}
                className={clsx(
                  'flex w-full cursor-pointer items-start gap-3 rounded-2xl border border-white/15 bg-white/40 p-4 transition hover:bg-white/60 dark:bg-white/10 dark:hover:bg-white/15',
                  updateMutation.isPending ? 'opacity-60' : ''
                )}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={Boolean(preferences[item.key])}
                  onChange={() => togglePreference(item.key)}
                  disabled={updateMutation.isPending}
                />
                <span>
                  <span className="text-sm font-semibold text-foreground">{item.title}</span>
                  <br />
                  <span className="text-xs text-muted-foreground">{item.description}</span>
                </span>
              </label>
            ))}
            {updateMutation.isSuccess && (
              <p className="text-xs font-medium text-[hsl(var(--success))]">Preferences saved successfully.</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
